/**
 * Actions Timeline Component
 * Displays a timeline of drill actions with elapsed time from drill start
 */
import { CheckCircle, XCircle, SkipForward } from 'lucide-react'
import PropTypes from 'prop-types'

import { Badge } from '@/components/elements/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { DRILL_STATES } from '@/types/drillTimer'

const formatElapsedTime = (recordTime, drillStartTime) => {
  if (!recordTime || !drillStartTime) return '—'
  const elapsed = new Date(recordTime) - new Date(drillStartTime)
  if (elapsed < 0) return '0:00'
  const totalSeconds = Math.floor(elapsed / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const formatActionName = (action) =>
  action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

const formatStateName = (state) => state.charAt(0).toUpperCase() + state.slice(1).toLowerCase()

const getStateIcon = (state) => {
  if (state === DRILL_STATES.COMPLETED || state === DRILL_STATES.STARTED) {
    return <CheckCircle className="h-4 w-4 text-green-600" />
  }
  if (state === DRILL_STATES.SKIPPED) {
    return <SkipForward className="h-4 w-4 text-red-600" />
  }
  return <XCircle className="h-4 w-4 text-gray-400" />
}

const getStateBadge = (state) => {
  const normalizedState = state?.toLowerCase()

  if (normalizedState === DRILL_STATES.COMPLETED || normalizedState === DRILL_STATES.STARTED) {
    return <Badge className="bg-green-600">{formatStateName(state)}</Badge>
  }
  if (normalizedState === DRILL_STATES.SKIPPED) {
    return <Badge variant="destructive">{formatStateName(state)}</Badge>
  }
  return <Badge variant="secondary">{formatStateName(state || 'Unknown')}</Badge>
}

export function ActionsTimeline({ records = [], startTime }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        {records.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center">No actions recorded</div>
        ) : (
          <div className="space-y-3">
            {records.map((record) => {
              const timestamp = record.time || record.timestamp
              const recordKey = `${record.action}-${record.state}-${timestamp}`
              return (
                <div
                  key={recordKey}
                  className="flex items-center gap-4 rounded-lg border p-3 hover:bg-gray-50"
                >
                  {getStateIcon(record.state)}
                  <div className="flex-1">
                    <div className="font-medium">{formatActionName(record.action)}</div>
                    {timestamp && (
                      <div className="text-muted-foreground text-sm">
                        {formatElapsedTime(timestamp, startTime)}
                      </div>
                    )}
                  </div>
                  {getStateBadge(record.state)}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

ActionsTimeline.propTypes = {
  records: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      time: PropTypes.string,
      timestamp: PropTypes.string,
    }),
  ),
  startTime: PropTypes.string.isRequired,
}
